import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openLink } from '@tma.js/sdk-react';
import { AlertCircle } from 'lucide-react';

import {
  useTournamentMatchDetailQuery,
  useMatchCheckinMutation,
} from '@/services/endpoints/matches';
import { useTournamentIdQuery } from '@/services/endpoints/tournaments';
import { useGetMeQuery } from '@/services/endpoints/auth';
import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { MatchCountdown } from '@/components/matches/MatchCountdown';
import { MatchUserBanner } from '@/components/matches/MatchUserBanner';
import { resolvePlayerName } from '@/lib/playerName';
import { EMatchStatus } from '@/types/enums';
import type { Participant } from '@/types/tournament';

/**
 * Match detail screen. Reached from TournamentDetail's matches list with
 * `?t=<tournamentId>` in the query string so we have both ids the Laravel
 * endpoint requires.
 *
 * Two render modes:
 *  - **Autohandled** (`tournament.is_autohandled`): Clash-Royale-style flow
 *    with status-driven UI — participant banners, countdown, score cards
 *    when COLLECTING/STARTED, and a single contextual action button:
 *      WAITING/OPENED no opponent → disabled "This match hasn't started yet"
 *      CHECKIN                    → "I'm ready to play" → POST /checkin
 *      STARTED/COLLECTING/PENDING → "Open Clash Royale and play" → deeplink
 *      CONFLICT                   → "Get support" → opens tournament.chat_link
 *      FINISHED                   → disabled "Match finished"
 *  - **Standard** (other tournament types): simpler status + result panel
 *    + chat link. Score entry still TODO — we just surface the data.
 *
 * Polls every 2s while the route is mounted, mirroring mobile-app
 * (autohandled tournaments race a few seconds at most so a slower poll
 * leaves the UI feeling stale).
 *
 * Web port of mobile-app/app/matchDetails/[id].tsx + autohandled_match.tsx.
 */
export function MatchDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const tournamentParam = search.get('t');
  const { t } = useTranslation('tournaments');
  const haptics = useHaptics();

  const matchId = Number(id);
  const tournamentId = Number(tournamentParam);
  const validIds =
    !!id && !!tournamentParam && !Number.isNaN(matchId) && !Number.isNaN(tournamentId);

  const { data: tournament } = useTournamentIdQuery(tournamentId, {
    skip: !validIds,
  });
  const { data: match, isLoading } = useTournamentMatchDetailQuery(
    { id: tournamentId, matchId },
    {
      skip: !validIds,
      pollingInterval: 2000,
    },
  );
  const { data: me } = useGetMeQuery();
  const [doCheckin, { isLoading: isCheckingIn }] = useMatchCheckinMutation();

  // Laravel returns the user id as number, but our slice types it loosely
  // as number|string|undefined. Coerce once so the participant resolvers
  // only deal with a clean number|null comparison.
  const meUserId = useMemo<number | null>(() => {
    if (me?.id === undefined || me?.id === null) return null;
    const n = typeof me.id === 'number' ? me.id : Number(me.id);
    return Number.isFinite(n) ? n : null;
  }, [me?.id]);

  const meParticipant = useMemo(
    () => resolveMe(match?.first_participant, match?.second_participant, meUserId),
    [match?.first_participant, match?.second_participant, meUserId],
  );
  const opponentParticipant = useMemo(
    () =>
      resolveOpponent(match?.first_participant, match?.second_participant, meUserId),
    [match?.first_participant, match?.second_participant, meUserId],
  );

  const handleCheckin = useCallback(async () => {
    if (!match) return;
    haptics.impact('light');
    try {
      await doCheckin({ tournamentId, matchId: match.id }).unwrap();
      haptics.notification('success');
    } catch {
      haptics.notification('error');
    }
  }, [doCheckin, haptics, match, tournamentId]);

  const handleOpenClashRoyale = useCallback(() => {
    haptics.impact('light');
    openLink.ifAvailable('https://link.clashroyale.com');
  }, [haptics]);

  const handleOpenDiscord = useCallback(() => {
    if (!tournament?.chat_link) return;
    haptics.impact('light');
    openLink.ifAvailable(tournament.chat_link);
  }, [haptics, tournament?.chat_link]);

  if (!validIds) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-sm text-fg-muted">
          {t('match_context_missing', {
            defaultValue:
              'Open this match from a tournament — match links need the tournament context.',
          })}
        </p>
      </div>
    );
  }

  if (isLoading && !match) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-sm text-danger">
          {t('match_not_found', { defaultValue: 'Match not found.' })}
        </p>
      </div>
    );
  }

  // ---- Autohandled branch (Clash Royale, etc.) ----
  if (tournament?.is_autohandled) {
    return (
      <AutohandledMatchView
        match={match}
        tournament={tournament}
        meParticipant={meParticipant}
        opponentParticipant={opponentParticipant}
        isCheckingIn={isCheckingIn}
        onCheckin={handleCheckin}
        onOpenClashRoyale={handleOpenClashRoyale}
        onOpenDiscord={handleOpenDiscord}
        t={t}
      />
    );
  }

  // ---- Standard branch (placeholder — score upload still TODO) ----
  const leftName = resolvePlayerName(match.first_participant) ?? 'TBD';
  const rightName = resolvePlayerName(match.second_participant) ?? 'TBD';
  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header>
        <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">
          {t('round', { defaultValue: 'Round' })} {match.round}
        </p>
        <h1 className="mt-1 text-2xl font-bold text-fg">
          {leftName}
          <span className="mx-2 text-fg-muted">vs</span>
          {rightName}
        </h1>
      </header>

      <section className="rounded-xl bg-bg-secondary p-4">
        <p className="text-xs uppercase tracking-wide text-fg-muted">
          {t('status', { defaultValue: 'Status' })}
        </p>
        <p className="mt-1 text-sm text-fg">{match.match_status}</p>
      </section>

      <section className="rounded-xl bg-bg-secondary p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('result', { defaultValue: 'Result' })}
        </h2>
        <p className="mt-2 text-sm text-fg">
          {match.match_result ?? t('result_pending', { defaultValue: 'Result pending.' })}
        </p>
      </section>
    </div>
  );
}

// ---------- Autohandled view ----------

const STATUS_COLOR: Record<string, string> = {
  [EMatchStatus.OPENED]: '#ffffff',
  [EMatchStatus.WAITING]: '#ffffff',
  [EMatchStatus.CHECKIN]: '#00E7DF',
  [EMatchStatus.STARTED]: '#FFC300',
  [EMatchStatus.COLLECTING]: '#FFC300',
  [EMatchStatus.FINISHED]: '#0F4F4C',
  [EMatchStatus.CONFLICT]: '#F46653',
  [EMatchStatus.PENDING]: '#FFC300',
};

const STATUS_TITLE = {
  [EMatchStatus.OPENED]: {
    title: 'autohandled.title.waiting',
    subtitle: 'autohandled.subtitle.waiting',
  },
  [EMatchStatus.WAITING]: {
    title: 'autohandled.title.waiting',
    subtitle: 'autohandled.subtitle.waiting',
  },
  [EMatchStatus.CHECKIN]: {
    title: 'autohandled.title.checkin',
    subtitle: 'autohandled.subtitle.checkin',
  },
  [EMatchStatus.STARTED]: {
    title: 'autohandled.title.collecting',
    subtitle: 'autohandled.subtitle.collecting',
  },
  [EMatchStatus.COLLECTING]: {
    title: 'autohandled.title.collecting',
    subtitle: 'autohandled.subtitle.collecting',
  },
  [EMatchStatus.FINISHED]: {
    title: 'autohandled.title.finished',
    subtitle: 'autohandled.subtitle.finished',
  },
  [EMatchStatus.CONFLICT]: {
    title: 'autohandled.title.conflict',
    subtitle: 'autohandled.subtitle.conflict',
  },
  [EMatchStatus.PENDING]: {
    title: 'autohandled.title.collecting',
    subtitle: 'autohandled.subtitle.collecting',
  },
} as const;

const MATCH_STANDARD = [
  EMatchStatus.OPENED,
  EMatchStatus.WAITING,
  EMatchStatus.CHECKIN,
  EMatchStatus.PENDING,
  EMatchStatus.FINISHED,
] as const;

type AutohandledProps = {
  match: NonNullable<ReturnType<typeof useTournamentMatchDetailQuery>['data']>;
  tournament: NonNullable<ReturnType<typeof useTournamentIdQuery>['data']>;
  meParticipant: Participant | null;
  opponentParticipant: Participant | null;
  isCheckingIn: boolean;
  onCheckin: () => void;
  onOpenClashRoyale: () => void;
  onOpenDiscord: () => void;
  t: ReturnType<typeof useTranslation>['t'];
};

function AutohandledMatchView({
  match,
  tournament,
  meParticipant,
  opponentParticipant,
  isCheckingIn,
  onCheckin,
  onOpenClashRoyale,
  onOpenDiscord,
  t,
}: AutohandledProps) {
  const status = match.match_status as EMatchStatus;
  const flagColor = STATUS_COLOR[status] ?? '#ffffff';

  // My position in the bracket (0 = first_participant, 1 = second).
  const myIndex = useMemo(() => {
    if (!meParticipant) return -1;
    if (match.first_participant?.id === meParticipant.id) return 0;
    if (match.second_participant?.id === meParticipant.id) return 1;
    return -1;
  }, [match.first_participant?.id, match.second_participant?.id, meParticipant]);

  const myCheckin =
    myIndex === 0
      ? match.first_participant_checkin
      : myIndex === 1
        ? match.second_participant_checkin
        : false;

  const titleKey = STATUS_TITLE[status]?.title ?? STATUS_TITLE[EMatchStatus.WAITING].title;
  const subtitleKey =
    STATUS_TITLE[status]?.subtitle ?? STATUS_TITLE[EMatchStatus.WAITING].subtitle;

  // Action button: text + handler + disabled state.
  const action = resolveAction({
    status,
    opponent: opponentParticipant,
    alreadyCheckedIn: !!myCheckin,
    isCheckingIn,
    chatLink: tournament.chat_link ?? null,
    onCheckin,
    onOpenClashRoyale,
    onOpenDiscord,
    t,
  });

  const isCollectingOrStarted =
    status === EMatchStatus.COLLECTING || status === EMatchStatus.STARTED;

  // First participant's banner colour: brand when they checked in during a
  // "standard" status; otherwise the status flag colour.
  const firstBorder =
    match.first_participant_checkin &&
    MATCH_STANDARD.includes(status as (typeof MATCH_STANDARD)[number])
      ? '#00E7DF'
      : flagColor;
  const secondBorder =
    match.second_participant_checkin &&
    MATCH_STANDARD.includes(status as (typeof MATCH_STANDARD)[number])
      ? '#00E7DF'
      : flagColor;

  const firstScore = parseScore(match.match_result, 0);
  const secondScore = parseScore(match.match_result, 1);

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-y-auto">
        {/* Round badge */}
        <header className="flex items-center justify-between px-5 pt-5">
          <h1 className="text-lg font-bold text-fg">
            {t('round', { defaultValue: 'Round' })} {match.round}
          </h1>
          <span
            className="rounded-md px-2 py-1 text-[11px] font-semibold uppercase"
            style={{ backgroundColor: flagColor, color: '#003A38' }}
          >
            {opponentParticipant ? match.match_status : 'WAITING'}
          </span>
        </header>

        {/* Participant banners — me on top if I'm first_participant */}
        <div className="mt-4">
          <MatchUserBanner
            participant={myIndex === 0 ? meParticipant : opponentParticipant}
            borderColor={firstBorder}
            badge={myIndex === 0 ? t('you', { defaultValue: 'YOU' }) : undefined}
            waitingLabel={t('waiting_for_opponent', {
              defaultValue: 'Waiting for opponent…',
            })}
          />

          {/* Score row appears only when actively playing */}
          {isCollectingOrStarted && (
            <div className="flex items-center justify-between gap-3 px-4 py-1">
              <ScoreCard score={myIndex === 0 ? firstScore : secondScore} />
              <div className="flex flex-col items-center">
                <span className="text-[12px] text-fg">
                  {t('win', { defaultValue: 'Win' })}
                </span>
                <span className="text-[12px] font-bold text-fg">
                  {match.games_count > 1
                    ? t('battles_plural', {
                        count: match.games_count,
                        defaultValue: '{{count}} Battles',
                      })
                    : t('battles_singular', {
                        count: match.games_count,
                        defaultValue: '{{count}} Battle',
                      })}
                </span>
              </div>
              <ScoreCard score={myIndex === 0 ? secondScore : firstScore} />
            </div>
          )}

          <MatchUserBanner
            participant={myIndex === 1 ? meParticipant : opponentParticipant}
            borderColor={secondBorder}
            badge={myIndex === 1 ? t('you', { defaultValue: 'YOU' }) : undefined}
            waitingLabel={t('waiting_for_opponent', {
              defaultValue: 'Waiting for opponent…',
            })}
          />
        </div>

        {/* Status section: title + countdown + subtitle */}
        <div className="mt-8 flex flex-col items-center px-12 pb-32 text-center">
          <h2 className="text-sm font-bold uppercase text-fg">
            {t(titleKey, { defaultValue: defaultTitleFor(status) })}
          </h2>

          {MATCH_STANDARD.includes(status as (typeof MATCH_STANDARD)[number]) &&
            !!match.countdown &&
            match.countdown > 0 && (
              <MatchCountdown
                countdown={match.countdown}
                size="large"
                className="mt-4"
              />
            )}

          <p className="mt-6 text-xs italic text-fg-muted">
            {t(subtitleKey, { defaultValue: defaultSubtitleFor(status) })}
          </p>
        </div>
      </div>

      {/* Bottom action bar (sticky to viewport bottom) */}
      <div className="sticky bottom-0 flex flex-col items-center gap-2 border-t border-border bg-bg-secondary px-4 pb-6 pt-4">
        {status === EMatchStatus.CONFLICT && (
          <p className="flex items-center gap-1 text-[11px] text-danger">
            <AlertCircle className="h-3.5 w-3.5" />
            {t('autohandled.conflict.hint', {
              defaultValue: 'Reach us on Discord for support.',
            })}
          </p>
        )}
        <button
          type="button"
          disabled={action.disabled || action.loading}
          onClick={action.onPress}
          className="flex h-12 w-full max-w-md items-center justify-center rounded-xl bg-brand text-sm font-bold uppercase text-brand-fg transition-opacity active:opacity-80 disabled:cursor-not-allowed disabled:bg-bg-tertiary disabled:text-fg-muted"
        >
          {action.loading
            ? t('autohandled.checking_in', { defaultValue: 'Checking in…' })
            : action.text}
        </button>
      </div>
    </div>
  );
}

// ---------- helpers ----------

function ScoreCard({ score }: { score: number }) {
  return (
    <div className="flex h-16 flex-1 items-center justify-center rounded-xl bg-bg-tertiary">
      <span className="text-3xl font-bold text-brand">{score}</span>
    </div>
  );
}

function resolveMe(
  first: Participant | null | undefined,
  second: Participant | null | undefined,
  meUserId: number | null,
): Participant | null {
  if (meUserId === null) return null;
  if (first?.profile?.user?.id === meUserId) return first;
  if (second?.profile?.user?.id === meUserId) return second;
  return null;
}

function resolveOpponent(
  first: Participant | null | undefined,
  second: Participant | null | undefined,
  meUserId: number | null,
): Participant | null {
  if (meUserId === null) return null;
  if (first?.profile?.user?.id === meUserId) return second ?? null;
  if (second?.profile?.user?.id === meUserId) return first ?? null;
  return null;
}

function parseScore(matchResult: string | null | undefined, idx: 0 | 1): number {
  if (!matchResult) return 0;
  const parts = matchResult.split(':');
  const raw = parts[idx];
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function defaultTitleFor(status: EMatchStatus): string {
  switch (status) {
    case EMatchStatus.CHECKIN:
      return 'Confirm that you are ready to play';
    case EMatchStatus.STARTED:
    case EMatchStatus.COLLECTING:
    case EMatchStatus.PENDING:
      return "Relax, I'm collecting the results…";
    case EMatchStatus.FINISHED:
      return 'Match status: finished';
    case EMatchStatus.CONFLICT:
      return 'Ops, our system is currently offline…';
    default:
      return 'Your opponent is playing the previous round';
  }
}

function defaultSubtitleFor(status: EMatchStatus): string {
  switch (status) {
    case EMatchStatus.CHECKIN:
      return "If you don't confirm this in time, you will automatically lose.";
    case EMatchStatus.STARTED:
    case EMatchStatus.COLLECTING:
    case EMatchStatus.PENDING:
      return 'If you have already played, just wait. If not, open Clash Royale and accept the battle!';
    case EMatchStatus.FINISHED:
      return 'The match has finished';
    case EMatchStatus.CONFLICT:
      return 'Reach us out on our Discord support server!';
    default:
      return 'Go get a coffee, but keep an eye on the QLASH App!';
  }
}

interface ActionResolution {
  text: string;
  onPress: () => void;
  disabled: boolean;
  loading: boolean;
}

function resolveAction(args: {
  status: EMatchStatus;
  opponent: Participant | null;
  alreadyCheckedIn: boolean;
  isCheckingIn: boolean;
  chatLink: string | null;
  onCheckin: () => void;
  onOpenClashRoyale: () => void;
  onOpenDiscord: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}): ActionResolution {
  const {
    status,
    opponent,
    alreadyCheckedIn,
    isCheckingIn,
    chatLink,
    onCheckin,
    onOpenClashRoyale,
    onOpenDiscord,
    t,
  } = args;
  const noop = () => {};

  if (status === EMatchStatus.CONFLICT) {
    return {
      text: t('autohandled.action.support', { defaultValue: 'Get support' }),
      onPress: chatLink ? onOpenDiscord : noop,
      disabled: !chatLink,
      loading: false,
    };
  }

  if (!opponent) {
    return {
      text: t('autohandled.action.no_opponent', {
        defaultValue: "This match hasn't started yet",
      }),
      onPress: noop,
      disabled: true,
      loading: false,
    };
  }

  if (status === EMatchStatus.CHECKIN) {
    return {
      text: t('autohandled.action.ready', { defaultValue: "I'm ready to play" }),
      onPress: onCheckin,
      disabled: alreadyCheckedIn || isCheckingIn,
      loading: isCheckingIn,
    };
  }

  if (
    status === EMatchStatus.STARTED ||
    status === EMatchStatus.COLLECTING ||
    status === EMatchStatus.PENDING
  ) {
    return {
      text: t('autohandled.action.open_cr', {
        defaultValue: 'Open Clash Royale and play',
      }),
      onPress: onOpenClashRoyale,
      disabled: false,
      loading: false,
    };
  }

  if (status === EMatchStatus.FINISHED) {
    return {
      text: t('autohandled.action.finished', { defaultValue: 'Match finished' }),
      onPress: noop,
      disabled: true,
      loading: false,
    };
  }

  return {
    text: t('autohandled.action.no_opponent', {
      defaultValue: "This match hasn't started yet",
    }),
    onPress: noop,
    disabled: true,
    loading: false,
  };
}
